/**
 * PBH / Exchanges data layer — RTK Query API and list-fetch router.
 *
 * This module is the **public entry point** for PBH order list and create flows.
 * It does two jobs:
 *
 * 1. **Route `getPbhOrders` requests** — `fetchPbhOrders` picks the correct
 *    `PBHOrdersSQLOnly` strategy from the argument shape. Every path uses the
 *    SQL-only OData entity (never legacy `odata/pbhorders`).
 * 2. **Expose RTK Query** — `pbhApi` wraps those fetches in RTK Query so the
 *    app gets caching, de-duplication, and tag-based invalidation without
 *    importing `@reduxjs/toolkit/query/react` hooks.
 *
 * Heavy action-filter scan logic (OData chunking, in-memory cache, abort,
 * sessionStorage resume) lives in `sqlOnlyActionScan.ts`. Shared types and
 * OData option builders live in `pbhOrdersSqlOnlyTypes.ts`. Both are re-exported
 * here so callers can keep importing from `pbhApi`.
 */
/* eslint-disable simple-import-sort/imports */
import config from "config";
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query";
import { apiService } from "./";
import { supportsSqlOnlyActionODataFilter } from "../views/Exchanges/utils/sqlOnlyActionFilterUtils";
import {
  buildPbhOrdersSqlOnlyQueryOptions,
  type GetPbhOrdersArgs,
  type GetPbhOrdersResponse,
  type PbhSort,
} from "./pbhOrdersSqlOnlyTypes";
import {
  fetchSqlOnlyActionFilteredPage,
  fetchSqlOnlyChunkedExport,
  extendSqlOnlyActionScanForPage,
  findSqlOnlyActionMatchPageSkip,
  getSqlOnlyActionScanCacheKey,
  invalidateSqlOnlyActionScanCache,
  sliceSqlOnlyActionFromCache,
  sliceSqlOnlyActionPageFromCache,
  SQL_ONLY_ACTION_MAX_CHUNK_REQUESTS,
  SQL_ONLY_ACTION_MAX_ODATA_SCAN,
  SQL_ONLY_ACTION_SCAN_CHUNK_SIZE,
  SQL_ONLY_EXPORT_CHUNK_SIZE,
  SQL_ONLY_MAX_EXPORT_ROWS,
  SQL_ONLY_ODATA_MAX_TOP,
  toSqlOnlyActionScanArgs,
  type SqlOnlyActionDeepLinkMatch,
  type SqlOnlyActionPageSlice,
} from "./sqlOnlyActionScan";

/** Request/response shapes for `getPbhOrders` and download helpers. */
export type { GetPbhOrdersArgs, GetPbhOrdersResponse, PbhSort };
/** Result types for action-filter deep links and cached page slices. */
export type { SqlOnlyActionDeepLinkMatch, SqlOnlyActionPageSlice };

/**
 * Re-exports used by Exchanges hooks and tests. Scan helpers are consumed
 * directly by `useExchangesPage` (bypassing RTK Query for action filters).
 */
export {
  extendSqlOnlyActionScanForPage,
  findSqlOnlyActionMatchPageSkip,
  getSqlOnlyActionScanCacheKey,
  invalidateSqlOnlyActionScanCache,
  sliceSqlOnlyActionFromCache,
  sliceSqlOnlyActionPageFromCache,
  SQL_ONLY_ACTION_MAX_CHUNK_REQUESTS,
  SQL_ONLY_ACTION_MAX_ODATA_SCAN,
  SQL_ONLY_ACTION_SCAN_CHUNK_SIZE,
  SQL_ONLY_EXPORT_CHUNK_SIZE,
  SQL_ONLY_MAX_EXPORT_ROWS,
  SQL_ONLY_ODATA_MAX_TOP,
  toSqlOnlyActionScanArgs,
};

/** Bound reference to `apiService.orderQueryODataSqlOnly` (PBHOrdersSQLOnly OData). */
const getSqlOnlyOrderQuery = () =>
  apiService.orderQueryODataSqlOnly.bind(apiService);

/**
 * Resolve a PBH list/export request to the correct `PBHOrdersSQLOnly` handler.
 *
 * All branches call `PBHOrdersSQLOnly` OData (via `orderQueryODataSqlOnly` or
 * `getPBHOrdersSqlOnly`), except invalid toolbar `action` values which return an
 * empty list without HTTP.
 *
 * Decision order (first match wins):
 *
 * | Condition | Handler | OData |
 * |-----------|---------|-------|
 * | Toolbar action (Waiting/Action/Open/Closed/Cancelled) + `rows === 0` | `fetchSqlOnlyChunkedExport` | Chunked `$filter` + client refine |
 * | Toolbar action + paginated list | `fetchSqlOnlyActionFilteredPage` | Scan chunks until UI page filled |
 * | Text search and/or inbound/outbound status | `orderQueryODataSqlOnly` | Single `$filter` query |
 * | Unknown `action` (not one of the five toolbar values) | — | **No request**; `{ data: [], count: 0 }` |
 * | Default + `rows === 0` (CSV download, no filters) | `fetchUnfilteredSqlOnlyExport` | Chunked `$top=50` up to 100 rows |
 * | Default (no action, no text/status filter) | `getPBHOrdersSqlOnly` | Paginated list, no `$filter` |
 */

type PbhSqlOnlyListFetch = (
  rows: number,
  skip: number,
  sort?: PbhSort,
  action?: unknown,
  options?: { includeCount?: boolean; user?: Record<string, unknown> },
) => Promise<{ data?: unknown[]; count?: number }>;

/**
 * Unfiltered CSV export (`rows === 0` from `downloadPBHOrders`).
 *
 * `rows: 0` is a download signal (“export mode”), not an OData page size.
 * Passing it through to `getPBHOrdersSqlOnly` used to produce `$top=0` (empty
 * result). Page in `$top=50` chunks up to `SQL_ONLY_MAX_EXPORT_ROWS` instead.
 *
 * Exported for unit tests (chunk skips + 100-row cap).
 */
export async function fetchUnfilteredSqlOnlyExport(
  args: GetPbhOrdersArgs,
  listFetch: PbhSqlOnlyListFetch,
): Promise<GetPbhOrdersResponse> {
  const data: unknown[] = [];
  let skip = 0;
  let count: number | undefined;

  while (data.length < SQL_ONLY_MAX_EXPORT_ROWS) {
    const result = await listFetch(
      SQL_ONLY_ODATA_MAX_TOP,
      skip,
      args.sort,
      undefined,
      {
        includeCount: count === undefined && args.includeCount !== false,
        user: args.user as Record<string, unknown> | undefined,
      },
    );
    const batch = Array.isArray(result?.data) ? result.data : [];
    if (count === undefined && typeof result?.count === "number") {
      count = result.count;
    }
    data.push(...batch);
    if (batch.length < SQL_ONLY_ODATA_MAX_TOP) {
      break;
    }
    skip += SQL_ONLY_ODATA_MAX_TOP;
  }

  const capped = data.slice(0, SQL_ONLY_MAX_EXPORT_ROWS);

  return {
    data: capped as GetPbhOrdersResponse["data"],
    count,
  };
}

async function fetchPbhOrders(
  args: GetPbhOrdersArgs,
): Promise<GetPbhOrdersResponse> {
  const orderQuery = getSqlOnlyOrderQuery();
  const listFetch = apiService.getPBHOrdersSqlOnly.bind(
    apiService,
  ) as PbhSqlOnlyListFetch;

  const hasStatusOrTextFilter =
    (args.property && args.query) ||
    args.inboundStatus ||
    args.outboundStatus;

  /** True for the five Exchanges toolbar action values (each has an OData prefilter). */
  const sqlOnlyAction =
    !!args.action && supportsSqlOnlyActionODataFilter(args.action);

  // --- Toolbar action filter (Waiting, Action, Open, Closed, Cancelled) ---
  if (sqlOnlyAction) {
    if (args.rows === 0) {
      // Download path: paginate OData in 50-row chunks, refine client-side.
      return fetchSqlOnlyChunkedExport(args, orderQuery);
    }
    // List path: scan OData chunks until the requested UI page can be sliced.
    return fetchSqlOnlyActionFilteredPage(args, orderQuery);
  }

  // --- Text and/or status filter (Customer PO, Shipped, Offcore Part, …) ---
  if (hasStatusOrTextFilter) {
    const orderQueryOptions = buildPbhOrdersSqlOnlyQueryOptions(args);
    const entireDataSet = args.rows === 0;

    const result = await orderQuery(
      orderQueryOptions,
      args.sort,
      entireDataSet,
    );

    return {
      data: Array.isArray(result?.data) ? result.data : [],
      count: result?.count,
    };
  }

  // --- Invalid action: not in the toolbar dropdown; never call OData ---
  if (args.action) {
    return { data: [], count: 0 };
  }

  // --- Default: unfiltered list / CSV export (PBHOrdersSQLOnly) ---
  if (args.rows === 0) {
    return fetchUnfilteredSqlOnlyExport(args, listFetch);
  }

  const result = await listFetch(
    args.rows,
    args.skip,
    args.sort,
    undefined,
    {
      includeCount: args.includeCount !== false,
      user: args.user as Record<string, unknown> | undefined,
    },
  );

  return {
    data: (result && result.data) || [],
    count: result?.count,
  };
}

/**
 * RTK Query API for PBH orders.
 *
 * Uses `fakeBaseQuery()` because HTTP (auth, errors, base URLs) stays in
 * `apiService`. Endpoints call `apiService` inside `queryFn` and return
 * `{ data }` or `{ error }` so RTK Query can cache and invalidate.
 *
 * Cache behaviour for `getPbhOrders`:
 * - Cache key = full `GetPbhOrdersArgs` (rows, skip, sort, filters, action).
 * - `providesTags: [{ type: "PbhOrders", id: "LIST" }]` — any mutation that
 *   invalidates that tag triggers refetch for active list subscribers.
 * - This codebase uses `pbhApi.endpoints.getPbhOrders.initiate/select`, not
 *   generated React hooks (React 17 constraint).
 *
 * Action-filter lists: `useExchangesPage` sets `suspendFetch: true` on
 * `usePBHOrders` and drives pagination via `extendSqlOnlyActionScanForPage`
 * instead of RTK Query, because scan state is session-scoped and shared
 * across page changes.
 */
export const pbhApi = createApi({
  reducerPath: "pbhApi",
  baseQuery: fakeBaseQuery(),
  endpoints: (builder) => ({
    /**
     * Exchanges / PBH list — routes through `fetchPbhOrders` above.
     * Returns `{ data: Order[], count?: number, scanComplete?: boolean }`.
     */
    getPbhOrders: builder.query<GetPbhOrdersResponse, GetPbhOrdersArgs>({
      async queryFn(args) {
        try {
          const response = await fetchPbhOrders(args);
          return { data: response };
        } catch (error: unknown) {
          return {
            error: { status: "CUSTOM_ERROR", error: String(error) },
          };
        }
      },
      providesTags: [{ type: "PbhOrders", id: "LIST" }],
    }),
    /**
     * Submit a new PBH/Exchange request. On success, invalidates the list tag
     * so open Exchanges views refetch.
     */
    createPbhRequest: builder.mutation<
      { success: boolean },
      { orderType: string; payload: Record<string, unknown> }
    >({
      async queryFn({ orderType, payload }) {
        try {
          interface RuntimeConfig {
            ADSAPI?: string;
          }
          const runtimeConfig = config as unknown as RuntimeConfig;
          const ADSAPI_BASE =
            runtimeConfig.ADSAPI ||
            (process.env as Record<string, string | undefined>)
              .REACT_APP_ADS_API ||
            "";
          const endpoint = `${ADSAPI_BASE}/api/${orderType}s`;
          await apiService.postNewRequest(endpoint, payload);
          return { data: { success: true } };
        } catch (error: unknown) {
          return {
            error: { status: "CUSTOM_ERROR", error: String(error) },
          };
        }
      },
      invalidatesTags: [{ type: "PbhOrders", id: "LIST" }],
    }),
  }),
  tagTypes: ["PbhOrders"],
});
